/**
 * Campaigns hooks (CRUD + lifecycle + CSV import).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useApiClient } from '../context';
import type { ApiError } from '../hc';
import { deleteJson, getJson, patchJson, postJson } from './_http';

/* ─── Campaigns — Wave 5d.1 (FULLY IMPLEMENTED) ───────────────────────────── */

/** Status state machine values mirrored from `campaigns.status`. */
export type CampaignStatus = 'draft' | 'active' | 'paused' | 'paused_auto' | 'finished' | 'error';
export type CampaignSourceType = 'salesnav' | 'linkedin' | 'csv';
export type CampaignStepKind = 'invite' | 'fu1' | 'fu2' | 'fu3';

export interface Campaign {
  id: string;
  userId: string;
  linkedinAccountId: string;
  businessId: string;
  name: string;
  description: string | null;
  status: CampaignStatus;
  pauseReason: string | null;
  errorReason: string | null;
  sourceType: CampaignSourceType;
  sourceUrl: string | null;
  csvImportId: string | null;
  dailyLimit: number;
  startedAt: string | number | null;
  invitHasMessage: boolean;
  leadsCount: number;
  invitedCount: number;
  acceptedCount: number;
  repliedCount: number;
  hotCount: number;
  daysRunning: number;
  createdAt: string | number;
  updatedAt: string | number;
  deletedAt: string | number | null;
}

export interface CampaignStep {
  id: string;
  userId: string;
  campaignId: string;
  stepKind: CampaignStepKind;
  enabled: boolean;
  delayDays: number;
  messageText: string;
  createdAt: string | number;
}

export interface CampaignDetailResponse {
  campaign: Campaign;
  steps: CampaignStep[];
}

/** SPEC-CAMPAIGN-LOOP §3.1 — create-time CSV parse outcome on the 201. */
export interface CsvParseOutcome {
  status: 'parsed' | 'errored';
  reason?: string;
  uniqueLeads?: number;
  seededRuns?: number;
}

export interface CreateCampaignResponse extends CampaignDetailResponse {
  /** Present only for CSV-sourced creates that ran the parse. */
  csvParse?: CsvParseOutcome;
}

export interface GenerateCampaignSequenceInput {
  linkedinAccountId: string;
  businessId: string;
  uiLang: 'fr' | 'en';
  objective?: string;
  audienceHint?: string;
}

export interface GeneratedCampaignSequence {
  steps: Record<CampaignStepKind, string>;
  evidence: { userAuthoredExamples: number };
  modelUsed: string;
  failedOver: boolean;
  costMicroUsd: number;
}

export interface GenerateCampaignSequenceResponse {
  generated: GeneratedCampaignSequence;
}

export interface CampaignsListResponse {
  campaigns: Campaign[];
}

export interface CreateCampaignInput {
  name: string;
  description?: string;
  linkedinAccountId: string;
  businessId: string;
  sourceType: CampaignSourceType;
  sourceUrl?: string;
  csvImportId?: string;
  dailyLimit?: number;
  invitHasMessage?: boolean;
  steps?: {
    stepKind: CampaignStepKind;
    enabled: boolean;
    delayDays: number;
    messageText: string;
  }[];
}

export interface UpdateCampaignInput {
  id: string;
  patch: {
    name?: string;
    description?: string;
    linkedinAccountId?: string;
    businessId?: string;
    sourceType?: CampaignSourceType;
    sourceUrl?: string;
    csvImportId?: string;
    dailyLimit?: number;
    invitHasMessage?: boolean;
    steps?: CreateCampaignInput['steps'];
  };
}

export interface LaunchCampaignResponse {
  workflowId: string;
  /** Search campaigns use the durable source job id as workflowId. */
  sourceJobId?: string;
  searchId?: string;
  sourceJob?: CampaignSourceJob;
  scheduledAt: number;
  status: 'running';
  /** §3.3 — runs in the cohort at launch time (0 while a search streams in). */
  seededRuns: number;
}

export type CampaignSourceJobStatus =
  | 'queued'
  | 'running'
  | 'retrying'
  | 'complete'
  | 'stalled'
  | 'error'
  | 'cancelled';

export interface CampaignSourceJob {
  id: string;
  userId: string;
  campaignId: string;
  linkedinAccountId: string;
  searchId: string;
  sourceType: 'salesnav' | 'linkedin';
  sourceUrl: string;
  normalizedSourceUrl: string;
  capLeads: number;
  status: CampaignSourceJobStatus;
  pagesReceived: number;
  leadsReceived: number;
  ingestedTotal: number;
  lastPageIndex: number | null;
  retryCount: number;
  lastError: string | null;
  heartbeatAt: string | number | null;
  startedAt: string | number | null;
  completedAt: string | number | null;
  nextRetryAt: string | number | null;
  createdAt: string | number;
  updatedAt: string | number;
}

export interface CampaignSourceJobsResponse {
  sourceJobs: CampaignSourceJob[];
}

export interface PauseCampaignResponse {
  campaign: Campaign;
}

export interface CsvImportRow {
  id: string;
  userId: string;
  filename: string;
  r2Key: string;
  totalRows: number;
  uniqueLeads: number;
  duplicates: number;
  urlColumn: string | null;
  status: 'pending' | 'parsed' | 'errored';
  errorReason: string | null;
  createdAt: string | number;
}

export interface CsvImportSignUploadInput {
  /** May be the literal `'draft'` while the campaign row doesn't exist yet. */
  campaignId: string;
  filename: string;
  contentType?: string;
}

export interface CsvImportSignUploadResponse {
  csvImportId: string;
  uploadUrl: string | null;
  key: string;
  expiresAt: number;
  maxBytes: number;
}

export interface CsvImportCompleteInput {
  campaignId: string;
  csvImportId: string;
  totalRows?: number;
  uniqueLeads?: number;
  duplicates?: number;
  urlColumn?: string;
}

export interface CsvImportCompleteResponse {
  csvImport: CsvImportRow;
  workflowId: string;
}

const campaignsKey = ['campaigns'] as const;
const campaignKey = (id: string) => ['campaign', id] as const;
const campaignSourceJobsKey = (id: string) => ['campaign-source-jobs', id] as const;

/** `GET /api/v1/campaigns` — list (filters out soft-deleted). */
export function useCampaigns(
  options: { enabled?: boolean } = {},
): UseQueryResult<CampaignsListResponse, ApiError> {
  const client = useApiClient();
  return useQuery<CampaignsListResponse, ApiError>({
    queryKey: campaignsKey,
    queryFn: () => getJson<CampaignsListResponse>(client, '/api/v1/campaigns'),
    enabled: options.enabled,
    retry: false,
  });
}

/** `GET /api/v1/campaigns/:id` — single envelope `{ campaign, steps }`. */
export function useCampaign(
  id: string | undefined,
  options: { enabled?: boolean } = {},
): UseQueryResult<CampaignDetailResponse, ApiError> {
  const client = useApiClient();
  const enabled = options.enabled ?? Boolean(id);
  return useQuery<CampaignDetailResponse, ApiError>({
    queryKey: campaignKey(id ?? '__none__'),
    queryFn: () =>
      getJson<CampaignDetailResponse>(client, `/api/v1/campaigns/${encodeURIComponent(id ?? '')}`),
    enabled,
    retry: false,
  });
}

/** `GET /api/v1/campaigns/:id/source-jobs` — durable search collection progress. */
export function useCampaignSourceJobs(
  id: string | undefined,
  options: { enabled?: boolean } = {},
): UseQueryResult<CampaignSourceJobsResponse, ApiError> {
  const client = useApiClient();
  const enabled = options.enabled ?? Boolean(id);
  return useQuery<CampaignSourceJobsResponse, ApiError>({
    queryKey: campaignSourceJobsKey(id ?? '__none__'),
    queryFn: () =>
      getJson<CampaignSourceJobsResponse>(
        client,
        `/api/v1/campaigns/${encodeURIComponent(id ?? '')}/source-jobs`,
      ),
    enabled,
    retry: false,
  });
}

/** `POST /api/v1/campaigns` — create (status `draft`). */
export function useCreateCampaign(): UseMutationResult<
  CreateCampaignResponse,
  ApiError,
  CreateCampaignInput
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<CreateCampaignResponse, ApiError, CreateCampaignInput>({
    mutationFn: (vars) => postJson<CreateCampaignResponse>(client, '/api/v1/campaigns', vars),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: campaignsKey });
    },
  });
}

/** `POST /api/v1/campaigns/generate-sequence` — tailored four-step message draft. */
export function useGenerateCampaignSequence(): UseMutationResult<
  GenerateCampaignSequenceResponse,
  ApiError,
  GenerateCampaignSequenceInput
> {
  const client = useApiClient();
  return useMutation<GenerateCampaignSequenceResponse, ApiError, GenerateCampaignSequenceInput>({
    mutationFn: (vars) =>
      postJson<GenerateCampaignSequenceResponse>(
        client,
        '/api/v1/campaigns/generate-sequence',
        vars,
      ),
  });
}

/** `PATCH /api/v1/campaigns/:id` — partial update + optional steps rewrite. */
export function useUpdateCampaign(): UseMutationResult<
  CampaignDetailResponse,
  ApiError,
  UpdateCampaignInput
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<CampaignDetailResponse, ApiError, UpdateCampaignInput>({
    mutationFn: (vars) =>
      patchJson<CampaignDetailResponse>(
        client,
        `/api/v1/campaigns/${encodeURIComponent(vars.id)}`,
        vars.patch,
      ),
    onSuccess: (data, vars) => {
      qc.setQueryData(campaignKey(vars.id), data);
      void qc.invalidateQueries({ queryKey: campaignsKey });
    },
  });
}

/** `DELETE /api/v1/campaigns/:id` — soft delete (US-138). */
export function useDeleteCampaign(): UseMutationResult<{ ok: true }, ApiError, { id: string }> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, { id: string }>({
    mutationFn: ({ id }) =>
      deleteJson<{ ok: true }>(client, `/api/v1/campaigns/${encodeURIComponent(id)}`),
    onSuccess: (_data, vars) => {
      qc.removeQueries({ queryKey: campaignKey(vars.id) });
      void qc.invalidateQueries({ queryKey: campaignsKey });
    },
  });
}

/** `POST /api/v1/campaigns/:id/launch` — flips draft → active. */
export function useLaunchCampaign(): UseMutationResult<
  LaunchCampaignResponse,
  ApiError,
  { id: string }
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<LaunchCampaignResponse, ApiError, { id: string }>({
    mutationFn: ({ id }) =>
      postJson<LaunchCampaignResponse>(
        client,
        `/api/v1/campaigns/${encodeURIComponent(id)}/launch`,
        { confirm: true },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: campaignKey(vars.id) });
      void qc.invalidateQueries({ queryKey: campaignSourceJobsKey(vars.id) });
      void qc.invalidateQueries({ queryKey: campaignsKey });
    },
  });
}

/** Explicit retry; only server-marked stalled/error jobs are eligible. */
export function useRetryCampaignSourceJob(): UseMutationResult<
  { sourceJob: CampaignSourceJob },
  ApiError,
  { campaignId: string; jobId: string }
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<
    { sourceJob: CampaignSourceJob },
    ApiError,
    { campaignId: string; jobId: string }
  >({
    mutationFn: ({ campaignId, jobId }) =>
      postJson<{ sourceJob: CampaignSourceJob }>(
        client,
        `/api/v1/campaigns/${encodeURIComponent(campaignId)}/source-jobs/${encodeURIComponent(jobId)}/retry`,
        {},
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: campaignSourceJobsKey(vars.campaignId) });
    },
  });
}

/** `POST /api/v1/campaigns/:id/pause` — flips active → paused. */
export function usePauseCampaign(): UseMutationResult<
  PauseCampaignResponse,
  ApiError,
  { id: string }
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<PauseCampaignResponse, ApiError, { id: string }>({
    mutationFn: ({ id }) =>
      postJson<PauseCampaignResponse>(
        client,
        `/api/v1/campaigns/${encodeURIComponent(id)}/pause`,
        {},
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: campaignKey(vars.id) });
      void qc.invalidateQueries({ queryKey: campaignsKey });
    },
  });
}

/** `POST /api/v1/campaigns/:id/resume` — flips paused/paused_auto → active. */
export function useResumeCampaign(): UseMutationResult<
  PauseCampaignResponse,
  ApiError,
  { id: string }
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<PauseCampaignResponse, ApiError, { id: string }>({
    mutationFn: ({ id }) =>
      postJson<PauseCampaignResponse>(
        client,
        `/api/v1/campaigns/${encodeURIComponent(id)}/resume`,
        {},
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: campaignKey(vars.id) });
      void qc.invalidateQueries({ queryKey: campaignsKey });
    },
  });
}

/** `POST /api/v1/campaigns/:id/csv-import/sign-upload` — R2 presign (US-127). */
export function useCsvImportSignUpload(): UseMutationResult<
  CsvImportSignUploadResponse,
  ApiError,
  CsvImportSignUploadInput
> {
  const client = useApiClient();
  return useMutation<CsvImportSignUploadResponse, ApiError, CsvImportSignUploadInput>({
    mutationFn: ({ campaignId, filename, contentType }) =>
      postJson<CsvImportSignUploadResponse>(
        client,
        `/api/v1/campaigns/${encodeURIComponent(campaignId)}/csv-import/sign-upload`,
        { filename, contentType: contentType ?? 'text/csv' },
      ),
  });
}

/** `POST /api/v1/campaigns/:id/csv-import/complete` — kicks the parse workflow stub. */
export function useCsvImportComplete(): UseMutationResult<
  CsvImportCompleteResponse,
  ApiError,
  CsvImportCompleteInput
> {
  const client = useApiClient();
  return useMutation<CsvImportCompleteResponse, ApiError, CsvImportCompleteInput>({
    mutationFn: ({ campaignId, csvImportId, totalRows, uniqueLeads, duplicates, urlColumn }) =>
      postJson<CsvImportCompleteResponse>(
        client,
        `/api/v1/campaigns/${encodeURIComponent(campaignId)}/csv-import/complete`,
        { csvImportId, totalRows, uniqueLeads, duplicates, urlColumn },
      ),
  });
}

/** `GET /api/v1/campaigns/:id/conversations` — top-8 spawned convs (US-137). */
export interface CampaignConversation {
  id: string;
  contactName: string;
  contactInitials: string;
  contactColor: string | null;
  isVeryHot: boolean | null;
  lastMessageAt: string | number | null;
}

export function useCampaignConversations(
  id: string | undefined,
  options: { enabled?: boolean } = {},
): UseQueryResult<{ conversations: CampaignConversation[] }, ApiError> {
  const client = useApiClient();
  const enabled = options.enabled ?? Boolean(id);
  return useQuery<{ conversations: CampaignConversation[] }, ApiError>({
    queryKey: ['campaign-conversations', id],
    queryFn: () =>
      getJson<{ conversations: CampaignConversation[] }>(
        client,
        `/api/v1/campaigns/${encodeURIComponent(id ?? '')}/conversations`,
      ),
    enabled,
    retry: false,
  });
}
